import type { FirebaseRepository } from '../models/FirebaseRepository'
import {
  createSlideDeck,
  fetchSlideDeck,
  saveSlideDeckForm,
  uploadSlideDeckImage,
} from '../firestore/SlideDeck'
import {
  fetchSlides,
  saveSlide,
  sortSlides,
  uploadSlideImage,
} from '../firestore/Slide'
import {
  deleteSlideDeckQuestion,
  fetchSlideQuestions,
  saveSlideDeckQuestion,
} from '../firestore/SlideQuestion'
import type { QuestionFieldsForUpload } from '../firestore/SlideQuestion'
import {
  fetchSlideDeckExhibits,
  saveSlideDeckExhibit,
  uploadSlideDeckExhibitImage,
} from '../firestore/SlideDeckExhibit'
import type { ExhibitFieldsForUpload } from '../firestore/SlideDeckExhibit'
import {
  deleteSlideRubric,
  fetchSlideRubrics,
  saveSlideRubric,
} from '../firestore/SlideRubric'
import {
  deleteSlideDeckMaterial,
  fetchSlideDeckMaterials,
  saveSlideDeckMaterial,
  uploadSlideDeckMaterialFile,
} from '../firestore/SlideDeckMaterial'
import {
  SlideDeckMaterialType,
  SlideDeckMaterialWorkType,
} from '../firestore/SlideDeckMaterial/schema'
import type { SlideDeckImportInput } from './SlideDeckImportSchema'
import { SlideType } from '../firestore/Slide/types'
import type { RubricType, SlideDeckType } from '../types'
import { SlideQuestionType } from '../models/SlideQuestionType'
import type { DocumentReference } from 'firebase/firestore'
import { fetchAllMedia } from '../firestore/Media'
import { SlideDeckStatus } from '../models/SlideDeck'
import { collection, doc } from 'firebase/firestore'
import { deleteDocWithError } from '../firestore-mobx/fetch'
import { VoiceOption } from '../models/VoiceOption'
import type { Media } from '../models/Media'
import { PRE_MEETING_QUESTION_SLIDE_ID } from '../constants'

/**
 * Get the correct mime type for a material based on its type.
 * Servers may return generic types like application/octet-stream,
 * so we infer the correct type from the material type or filename.
 */
function getMimeTypeForMaterial(
  materialType: SlideDeckMaterialType,
  blobType: string,
  filename: string
): string {
  // If the blob has a specific type (not generic), trust it
  if (blobType && blobType !== 'application/octet-stream') {
    return blobType
  }

  // Infer from material type
  switch (materialType) {
    case SlideDeckMaterialType.pdf:
      return 'application/pdf'
    case SlideDeckMaterialType.mp3:
      return 'audio/mpeg'
    case SlideDeckMaterialType.webm:
      return 'video/webm'
    case SlideDeckMaterialType.mp4:
      return 'video/mp4'
    case SlideDeckMaterialType.img:
    case SlideDeckMaterialType.featuredLarge: {
      // Try to infer from filename extension
      const ext = filename.split('.').pop()?.toLowerCase()
      if (ext === 'png') return 'image/png'
      if (ext === 'gif') return 'image/gif'
      if (ext === 'webp') return 'image/webp'
      return 'image/jpeg' // default for images
    }
    default:
      return blobType || 'application/octet-stream'
  }
}

/**
 * Parse a data URI and return a File object.
 * Data URIs have the format: data:[<mediatype>][;base64],<data>
 */
function parseDataUri(dataUri: string, defaultFilename: string): File | null {
  const match = dataUri.match(/^data:([^;,]+)(;base64)?,(.*)$/)
  if (!match) {
    return null
  }

  const mimeType = match[1] || 'application/octet-stream'
  const isBase64 = !!match[2]
  const data = match[3]

  let arrayBuffer: ArrayBuffer
  if (isBase64) {
    const binaryString = atob(data)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    arrayBuffer = bytes.buffer as ArrayBuffer
  } else {
    // URL-encoded data
    const decoded = decodeURIComponent(data)
    const encoder = new TextEncoder()
    arrayBuffer = encoder.encode(decoded).buffer as ArrayBuffer
  }

  // Infer extension from mime type
  const extMap: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
  }
  const ext = extMap[mimeType] || 'bin'
  const filename = defaultFilename.includes('.')
    ? defaultFilename
    : `${defaultFilename}.${ext}`

  return new File([arrayBuffer], filename, { type: mimeType })
}

/**
 * Fetch an image from a URL or parse a data URI and return a File object.
 * Returns null if the fetch fails or the data URI is invalid.
 */
async function fetchImageAsFile(
  urlOrDataUri: string,
  defaultFilename: string
): Promise<File | null> {
  // Handle data URIs
  if (urlOrDataUri.startsWith('data:')) {
    return parseDataUri(urlOrDataUri, defaultFilename)
  }

  // Handle regular URLs
  const response = await fetch(urlOrDataUri)
  if (!response.ok) {
    console.warn(
      `Failed to fetch image from ${urlOrDataUri}: ${response.status}`
    )
    return null
  }

  const blob = await response.blob()
  const filename = urlOrDataUri.split('/').pop() ?? defaultFilename
  const mimeType = blob.type.startsWith('image/') ? blob.type : 'image/jpeg'
  return new File([blob], filename, { type: mimeType })
}

/**
 * Fetch a material file from a URL or parse a data URI and return a File object.
 * Uses getMimeTypeForMaterial for proper type inference.
 * Returns null if the fetch fails or the data URI is invalid.
 */
async function fetchMaterialAsFile(
  urlOrDataUri: string,
  materialType: SlideDeckMaterialType,
  defaultFilename: string
): Promise<File | null> {
  // Handle data URIs
  if (urlOrDataUri.startsWith('data:')) {
    return parseDataUri(urlOrDataUri, defaultFilename)
  }

  // Handle regular URLs
  const response = await fetch(urlOrDataUri)
  if (!response.ok) {
    console.warn(
      `Failed to fetch material from ${urlOrDataUri}: ${response.status}`
    )
    return null
  }

  const blob = await response.blob()
  const filename = urlOrDataUri.split('/').pop() ?? defaultFilename
  const mimeType = getMimeTypeForMaterial(materialType, blob.type, filename)
  return new File([blob], filename, { type: mimeType })
}

export type ImportSummary = {
  slideDeckId: string
  idMap: {
    slide: Record<string, string>
    question: Record<string, string>
    exhibit: Record<string, string>
    rubric: Record<string, string>
    material: Record<string, string>
  }
  stats: {
    slides: number
    questions: number
    exhibits: number
    rubrics: number
    materials: number
  }
}

/**
 * Stub importer for TDD. Will be implemented to:
 * - create slide deck, slides, questions, exhibits
 * - map fake IDs to real IDs
 * - handle pointer fields
 */
export async function importSlideDeckJson(
  repository: FirebaseRepository,
  parsed: SlideDeckImportInput,
  opts?: { slideDeckId?: string }
): Promise<ImportSummary> {
  // helper to map Solo Bot type string to SlideQuestionType
  const soloTypeToQuestionType = (
    t:
      | 'Solo Moderator Bot'
      | 'Solo Tutor Bot'
      | 'Solo Grader Bot'
      | 'Solo Role Play Bot'
  ): SlideQuestionType => {
    switch (t) {
      case 'Solo Moderator Bot':
        return SlideQuestionType.moderatorBot
      case 'Solo Tutor Bot':
        return SlideQuestionType.tutorBot
      case 'Solo Grader Bot':
        return SlideQuestionType.graderBot
      case 'Solo Role Play Bot':
        return SlideQuestionType.rolePlayBot
    }
  }

  // helper to validate and coerce voice id
  const toVoiceOption = (v?: string | null): VoiceOption | undefined =>
    v && (Object.values(VoiceOption) as string[]).includes(v)
      ? (v as VoiceOption)
      : undefined

  // 1) If slideDeckId provided, verify draft and reset contents; else create a new deck
  let slideDeckId: string
  let slideDeckPrice = 150
  let slideDeckVersion = 'Imported'
  let slideDeckImageURL = ''
  if (opts?.slideDeckId) {
    const deck = await fetchSlideDeck(repository, {
      slideDeckId: opts.slideDeckId,
    })
    if (deck.slideDeckStatus !== SlideDeckStatus.draft) {
      throw new Error('Slide deck must be in draft state to import')
    }
    slideDeckId = opts.slideDeckId
    slideDeckPrice = deck.data.slideDeckPrice
    slideDeckVersion = deck.data.slideDeckVersion
    slideDeckImageURL = deck.data.slideDeckImageURL
      ? deck.data.slideDeckImageURL
      : ''
    // destructive reset: delete questions, exhibits, rubrics, materials, then slides
    const [questions, exhibits, slides, rubrics, materials] = await Promise.all(
      [
        fetchSlideQuestions(repository, { slideDeckId }),
        fetchSlideDeckExhibits(repository, { slideDeckId }),
        fetchSlides(repository, { slideDeckId }),
        fetchSlideRubrics(repository, { slideDeckId }),
        fetchSlideDeckMaterials(repository, { slideDeckId }),
      ]
    )

    await Promise.all(
      questions.map((q) =>
        deleteSlideDeckQuestion(repository, {
          slideDeckId,
          questionId: q.id,
        })
      )
    )
    // delete rubrics
    await Promise.all(
      rubrics.map((r) =>
        deleteSlideRubric(repository, {
          slideDeckId,
          slideRubricId: r.id,
        })
      )
    )
    // delete materials
    await Promise.all(
      materials.map((m) =>
        deleteSlideDeckMaterial(repository, {
          slideDeckId,
          materialId: m.id,
          materialType: m.data.materialType,
        })
      )
    )
    // delete exhibits (Firestore docs only; do not attempt storage deletes here)
    const exCol = collection(
      repository.firestore,
      'slide_deck',
      slideDeckId,
      'exhibit'
    )
    await Promise.all(
      exhibits.map((e) =>
        deleteDocWithError(doc(exCol, e.id), 'ImporterResetDeleteExhibitError')
      )
    )
    // delete slides (Firestore docs only to avoid storage permission issues in tests)
    const slCol = collection(
      repository.firestore,
      'slide_deck',
      slideDeckId,
      'slide'
    )
    await Promise.all(
      slides.map((s) =>
        deleteDocWithError(doc(slCol, s.id), 'ImporterResetDeleteSlideError')
      )
    )
  } else {
    slideDeckId = await createSlideDeck(repository)
  }

  await saveSlideDeckForm(repository, slideDeckId, {
    slideDeckName: parsed.slideDeckName,
    slideDeckDescription: parsed.slideDeckDescription,
    slideDeckTeaser: parsed.slideDeckTeaser,
    slideDeckDisciplines: parsed.slideDeckDisciplines,
    slideDeckLearningObjectives: parsed.slideDeckLearningObjectives,
    slideDeckKeyConcepts: parsed.slideDeckKeyConcepts ?? [],
    slideDeckPhraseSet: parsed.slideDeckPhraseSet ?? [],
    slideDeckType: (parsed.slideDeckType as unknown as SlideDeckType) ?? 0,
    slideDeckFeatured: false,
    slideDeckGoogleTemplateURL: '',
    slideDeckIndustries: [],
    slideDeckTags: [],
    slideDeckPrice: slideDeckPrice,
    slideDeckVersion: slideDeckVersion,
    slideDeckImageURL: slideDeckImageURL,
    primaryDesignerUserId: parsed.primaryDesignerUserId,
    designedForInstructorUserId: parsed.designedForInstructorUserId,
  })

  const idMap = {
    slide: {} as Record<string, string>,
    question: {} as Record<string, string>,
    exhibit: {} as Record<string, string>,
    rubric: {} as Record<string, string>,
    material: {} as Record<string, string>,
  }

  let questionCount = 0

  // Preload all media and index by title for globalAssetName lookups
  const mediaByTitle = new Map<string, Media>()
  try {
    const allMedia = await fetchAllMedia(repository)
    for (const m of allMedia) {
      // exact title match map
      mediaByTitle.set(m.data.mediaTitle, m)
    }
  } catch (err) {
    console.warn('Failed to fetch media for import asset linking', err)
    // If media fetch fails (e.g., no permission), proceed without media linking
  }

  // helper to build slide payload from import slide
  const buildSlidePayload = (
    s: SlideDeckImportInput['slides'][number]
  ): Omit<Parameters<typeof saveSlide>[1], 'slideDeckId'> => {
    const base: Omit<Parameters<typeof saveSlide>[1], 'slideDeckId'> = {
      slideDescription: s.slideDescription ?? '',
      slideDuration: s.slideDuration,
      slideName: s.slideName,
      slideType: s.slideType as unknown as SlideType,
      slideImageAltText: s.slideImageAltText ?? undefined,
      slideMarkdown: s.slideMarkdown ?? undefined,
      isSkippable: s.allowSkipVideo,
    }

    if (
      (s.slideType as number) === SlideType.animatedCards &&
      s.animatedCards
    ) {
      base.slideAnimatedCardsTitle = s.animatedCards.contentTitle
      base.slideAnimatedCardsDescription = s.animatedCards.contentDescription
      base.slideAnimatedCardsItems = s.animatedCards.cards.map((c) => ({
        title: c.cardTitle,
        description: c.cardDescription,
      }))
      base.slideAnimatedCardsSubtext = s.animatedCards.contentFooter
    }

    return base
  }

  // 2) Create slides in order and attach interactive elements
  const createdSlideIds: string[] = []
  for (const slide of parsed.slides) {
    const trimmedAssetName = (slide.globalAssetName ?? '').trim()
    const matchedMediaId =
      trimmedAssetName.length > 0
        ? mediaByTitle.get(trimmedAssetName)?.id
        : undefined
    // create slide
    const slideRef = await saveSlide(repository, {
      slideDeckId,
      ...buildSlidePayload(slide),
      mediaId: matchedMediaId,
      // avoid per-insert auto-reorder; we'll batch-order at the end
      skipAutoReorder: true,
    })
    const newSlideId = (slideRef as DocumentReference).id
    idMap.slide[slide.slideId] = newSlideId
    createdSlideIds.push(newSlideId)

    // Upload slide background image if provided (for basic slides)
    if (slide.slideImageURL && slide.slideImageURL.trim().length > 0) {
      try {
        const file = await fetchImageAsFile(
          slide.slideImageURL,
          'slide-image.jpg'
        )
        if (file) {
          await uploadSlideImage(repository, {
            slideDeckId,
            slideId: newSlideId,
            file,
          })
        }
      } catch (err) {
        console.warn(
          `Failed to download/upload slide image from ${slide.slideImageURL}:`,
          err
        )
      }
    }

    // talk track (group) -> questionType.talkTrack
    if (
      slide.slideGroupTalkTrack &&
      slide.slideGroupTalkTrack.trim().length > 0
    ) {
      const q = await saveSlideDeckQuestion(repository, {
        slideDeckId,
        questionFields: {
          question: slide.slideGroupTalkTrack,
          questionType: SlideQuestionType.talkTrack,
          answers: [],
          order: 0,
          slideId: newSlideId,
        },
      })
      idMap.question[(slide.slideId ?? '') + ':talkTrack'] = (
        q as DocumentReference
      ).id
      questionCount += 1
    }

    // slide heading -> questionType.title
    if (slide.slideHeading && slide.slideHeading.trim().length > 0) {
      const q = await saveSlideDeckQuestion(repository, {
        slideDeckId,
        questionFields: {
          question: slide.slideHeading,
          questionType: SlideQuestionType.title,
          answers: [],
          order: 0,
          slideId: newSlideId,
        },
      })
      idMap.question[(slide.slideId ?? '') + ':title'] = (
        q as DocumentReference
      ).id
      questionCount += 1
    }

    // moderation tasks -> questionType.moderationTask
    if (slide.moderationTasks && slide.moderationTasks.length > 0) {
      for (const [i, t] of slide.moderationTasks.entries()) {
        const q = await saveSlideDeckQuestion(repository, {
          slideDeckId,
          questionFields: {
            question: t.ask,
            prompt: t.additionalInstructions,
            questionType: SlideQuestionType.moderationTask,
            answers: [],
            order: i,
            slideId: newSlideId,
          },
        })
        idMap.question[(slide.slideId ?? '') + ':moderation:' + t.ask] = (
          q as DocumentReference
        ).id
        questionCount += 1
      }
      // solo bot -> slide question of specific bot type with optional prompt and voice
      if (slide.soloBot) {
        const q = await saveSlideDeckQuestion(repository, {
          slideDeckId,
          questionFields: {
            question: slide.soloBot.talkTrack ?? '',
            prompt: slide.soloBot.additionalInstructions ?? undefined,
            questionType: soloTypeToQuestionType(slide.soloBot.type),
            answers: [],
            order: 0,
            slideId: newSlideId,
            voiceId: toVoiceOption(slide.soloBot.voiceId),
          },
        })
        idMap.question[(slide.slideId ?? '') + ':soloBot'] = (
          q as DocumentReference
        ).id
        questionCount += 1
      }
    }

    // poll support (customPoll for multi-option)
    if (slide.slideType === SlideType.interactivePoll && slide.pollQuestion) {
      const isMulti =
        Array.isArray(slide.pollAnswers) && slide.pollAnswers.length > 0
      const questionFields: QuestionFieldsForUpload = {
        question: slide.pollQuestion,
        answers: isMulti ? slide.pollAnswers! : [],
        questionType: isMulti
          ? SlideQuestionType.customPoll
          : SlideQuestionType.poll,
        order: 0,
        slideId: newSlideId,
      }
      const q = await saveSlideDeckQuestion(repository, {
        slideDeckId,
        questionFields,
      })
      const newQuestionId = (q as DocumentReference).id
      idMap.question[(slide.slideId ?? '') + ':poll'] = newQuestionId
      questionCount += 1

      // update slide to attach input question ids
      await saveSlide(repository, {
        slideDeckId,
        ...buildSlidePayload(slide),
        slideId: newSlideId,
        mediaId: matchedMediaId,
        inputQuestionIds: [newQuestionId],
      })
    }

    // quiz questions support (for interactiveQuiz, groupQuiz, endOfSession slides)
    const quizValidSlideTypes = [
      SlideType.interactiveQuiz,
      SlideType.groupQuiz,
      SlideType.endOfSession,
    ]
    if (slide.quizQuestions && slide.quizQuestions.length > 0) {
      // Validate slide type
      if (!quizValidSlideTypes.includes(slide.slideType as SlideType)) {
        throw new Error(
          `Quiz questions can only be added to slide types 3 (interactiveQuiz), 10 (groupQuiz), or 12 (endOfSession). Got type ${slide.slideType} for slide ${slide.slideId}`
        )
      }

      const quizQuestionIds: string[] = []
      for (const [i, qq] of slide.quizQuestions.entries()) {
        const qRef = await saveSlideDeckQuestion(repository, {
          slideDeckId,
          questionFields: {
            question: qq.question,
            answers: qq.answers ?? [],
            correct: qq.correct,
            explanation: qq.explanation ?? undefined,
            order: qq.order ?? i,
            slideId: newSlideId,
            questionType:
              (qq.questionType as unknown as SlideQuestionType) ??
              SlideQuestionType.multipleChoice,
          },
        })
        const quizQId = (qRef as DocumentReference).id
        idMap.question[`${slide.slideId}:quiz:${qq.questionId}`] = quizQId
        quizQuestionIds.push(quizQId)
        questionCount += 1
      }

      // Update slide to attach input question ids
      await saveSlide(repository, {
        slideDeckId,
        ...buildSlidePayload(slide),
        slideId: newSlideId,
        mediaId: matchedMediaId,
        inputQuestionIds: quizQuestionIds,
      })
    }
  }

  // after creating all slides, enforce slide order to match JSON index
  if (createdSlideIds.length > 0) {
    await sortSlides(repository, {
      slideDeckId,
      currentOrder: createdSlideIds,
    })
  }

  // 3) Exhibits with slide pointer mapping
  for (const e of parsed.slideDeckExhibits) {
    const slideId = idMap.slide[e.SlideId]
    const trimmedAssetName = (e.globalAssetName ?? '').trim()
    // Only match media when an image URL exists (ensure exhibit media is an image)
    const mediaCandidate =
      trimmedAssetName.length > 0
        ? mediaByTitle.get(trimmedAssetName)
        : undefined
    let matchedMediaUrl: string | undefined
    let matchedMediaId: string | undefined
    // Only set matches if the candidate is an image (has mediaImageURL)
    if (mediaCandidate && 'mediaImageURL' in mediaCandidate.data) {
      matchedMediaUrl = mediaCandidate.data.mediaImageURL
      matchedMediaId = mediaCandidate.id
    }
    const exhibitFields: ExhibitFieldsForUpload = {
      exhibitDescription: e.exhibitDescription,
      exhibitName: e.exhibitName,
      slideId,
      exhibitAltText: e.imageAltText ? e.imageAltText : undefined,
      exhibitMarkdown: e.exhibitMarkdown ? e.exhibitMarkdown : undefined,
      exhibitURL: matchedMediaUrl,
      mediaId: matchedMediaId,
      inputSources: [],
    }
    const exRef = await saveSlideDeckExhibit(repository, {
      slideDeckId,
      exhibitFields,
    })
    const newExhibitId = (exRef as DocumentReference).id
    idMap.exhibit[e.exhibitId] = newExhibitId

    // Upload exhibit image if exhibitImageURL is provided (and no globalAssetName)
    if (
      !matchedMediaId &&
      e.exhibitImageURL &&
      e.exhibitImageURL.trim().length > 0
    ) {
      try {
        const file = await fetchImageAsFile(
          e.exhibitImageURL,
          'exhibit-image.jpg'
        )
        if (file) {
          await uploadSlideDeckExhibitImage(repository, {
            slideDeckId,
            exhibitId: newExhibitId,
            file,
          })
        }
      } catch (err) {
        console.warn(
          `Failed to download/upload exhibit image from ${e.exhibitImageURL}:`,
          err
        )
      }
    }
  }

  // 4) Pre-quiz questions (slideId fixed)
  for (const [i, q] of parsed.slideDeckPreQuizQuestions.entries()) {
    const qRef = await saveSlideDeckQuestion(repository, {
      slideDeckId,
      questionFields: {
        question: q.question,
        answers: q.answers ?? [],
        correct: q.correct,
        explanation: q.explanation ?? undefined,
        order: i,
        slideId: PRE_MEETING_QUESTION_SLIDE_ID,
        questionType:
          (q.questionType as unknown as SlideQuestionType) ??
          SlideQuestionType.multipleChoice,
      },
    })
    idMap.question[q.questionId] = (qRef as DocumentReference).id
    questionCount += 1
  }

  // 5) Rubrics with slide pointer mapping (optional)
  for (const r of parsed.slideDeckRubrics ?? []) {
    // Map fake slide IDs to real IDs
    const startSlideId = r.startSlideId ? idMap.slide[r.startSlideId] : ''
    const slideId = r.slideId ? idMap.slide[r.slideId] : ''

    const rubricRef = await saveSlideRubric(repository, {
      slideDeckId,
      rubricFields: {
        rubric: r.rubric,
        rubricDescription: r.rubricDescription,
        rubricPrompt: r.rubricPrompt ?? '',
        rubricType: r.rubricType as RubricType,
        rubricLabel0: r.rubricLabel0,
        rubricLabel1: r.rubricLabel1,
        startSlideId,
        slideId,
      },
    })
    idMap.rubric[r.rubricId] = (rubricRef as DocumentReference).id
  }

  // 6) Materials import
  for (const m of parsed.slideDeckMaterials ?? []) {
    // Resolve globalAssetName first - if found, we'll use global media instead of materialLink
    const trimmedAssetName = (m.globalAssetName ?? '').trim()
    const matchedMedia =
      trimmedAssetName.length > 0
        ? mediaByTitle.get(trimmedAssetName)
        : undefined
    const resolvedGlobalMediaId = matchedMedia?.id

    // Extract materialLink from global media based on material type
    let resolvedMaterialLink = ''
    if (matchedMedia) {
      if (
        m.materialType === SlideDeckMaterialType.pdf &&
        'mediaPdfURL' in matchedMedia.data
      ) {
        resolvedMaterialLink = matchedMedia.data.mediaPdfURL ?? ''
      } else if (
        (m.materialType === SlideDeckMaterialType.featuredLarge ||
          m.materialType === SlideDeckMaterialType.img) &&
        'mediaImageURL' in matchedMedia.data
      ) {
        resolvedMaterialLink = matchedMedia.data.mediaImageURL ?? ''
      } else if (
        (m.materialType === SlideDeckMaterialType.webm ||
          m.materialType === SlideDeckMaterialType.mp4) &&
        'mediaVideoURL' in matchedMedia.data
      ) {
        // Prefer streaming URL if available, fall back to regular video URL
        resolvedMaterialLink =
          ('mediaVideoStreamingURL' in matchedMedia.data &&
            matchedMedia.data.mediaVideoStreamingURL) ||
          matchedMedia.data.mediaVideoURL ||
          ''
      }
    }

    // Create the material document first
    const materialRef = await saveSlideDeckMaterial(repository, {
      slideDeckId,
      materialFields: {
        materialName: m.materialName,
        materialDescription: m.materialDescription ?? '',
        materialType: m.materialType,
        materialWorkType:
          m.materialWorkType ?? SlideDeckMaterialWorkType.neither,
        viewableByInstructor: m.viewableByInstructor ?? true,
        viewableByPublic: m.viewableByPublic ?? false,
        viewableByStudent: m.viewableByStudent ?? true,
        // For URL type, store materialLink directly; for global media, use resolved URL; otherwise set after upload
        materialLink:
          m.materialType === SlideDeckMaterialType.url
            ? (m.materialLink ?? '')
            : resolvedMaterialLink,
        // Use resolved globalMediaId
        globalMediaId: resolvedGlobalMediaId ?? '',
      },
    })
    const materialId = (materialRef as DocumentReference).id
    idMap.material[m.materialId] = materialId

    // For non-URL types with materialLink, download and upload the file
    // Skip if globalMediaId is provided (use global media instead)
    if (
      !resolvedGlobalMediaId &&
      m.materialType !== SlideDeckMaterialType.url &&
      m.materialLink &&
      m.materialLink.trim().length > 0
    ) {
      try {
        const file = await fetchMaterialAsFile(
          m.materialLink,
          m.materialType,
          'material'
        )
        if (file) {
          await uploadSlideDeckMaterialFile(repository, {
            slideDeckId,
            materialId,
            materialType: m.materialType,
            file,
          })
        }
      } catch (err) {
        console.warn(`Failed to download material from ${m.materialLink}:`, err)
      }
    }

    // For podcasts with imageUrl, download and upload the cover image
    if (
      m.materialType === SlideDeckMaterialType.mp3 &&
      m.imageUrl &&
      m.imageUrl.trim().length > 0
    ) {
      try {
        const response = await fetch(m.imageUrl)
        if (response.ok) {
          const blob = await response.blob()
          const filename = m.imageUrl.split('/').pop() ?? 'cover.jpg'
          // Ensure it's identified as an image
          const mimeType = blob.type.startsWith('image/')
            ? blob.type
            : 'image/jpeg'
          const file = new File([blob], filename, { type: mimeType })
          await uploadSlideDeckMaterialFile(repository, {
            slideDeckId,
            materialId,
            // Use img type for the podcast cover upload - this targets the imageUrl field
            materialType: SlideDeckMaterialType.img,
            file,
          })
        } else {
          console.warn(
            `Failed to download podcast cover image from ${m.imageUrl}: ${response.status}`
          )
        }
      } catch (err) {
        console.warn(
          `Failed to download podcast cover image from ${m.imageUrl}:`,
          err
        )
      }
    }
  }

  // 7) Slide deck cover image from URL or data URI
  if (parsed.slideDeckImageURL && parsed.slideDeckImageURL.trim().length > 0) {
    try {
      const file = await fetchImageAsFile(parsed.slideDeckImageURL, 'cover.jpg')
      if (file) {
        await uploadSlideDeckImage(repository, {
          slideDeckId,
          file,
        })
      }
    } catch (err) {
      console.warn(
        `Failed to download slide deck cover image from ${parsed.slideDeckImageURL}:`,
        err
      )
    }
  }

  return {
    slideDeckId,
    idMap,
    stats: {
      slides: Object.keys(idMap.slide).length,
      exhibits: Object.keys(idMap.exhibit).length,
      questions: questionCount,
      rubrics: Object.keys(idMap.rubric).length,
      materials: Object.keys(idMap.material).length,
    },
  }
}
